// src/services/pwaService.js

class PWAService {
  constructor() {
    this.deferredPrompt = null;
    this.installListeners = [];
    this.updateListeners = [];
    this.offlineListeners = [];
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    
    console.log('Initializing PWA Service');
    
    // Capture beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('beforeinstallprompt event captured in service');
      e.preventDefault();
      this.deferredPrompt = e;
      this.notifyInstallReady();
    });

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      console.log('App was installed');
      this.deferredPrompt = null;
      this.notifyAppInstalled();
    });

    // Listen for online/offline
    window.addEventListener('online', () => {
      console.log('App is online');
    });

    window.addEventListener('offline', () => {
      console.log('App is offline');
      this.notifyOffline();
    });

    // Check if already installed
    this.checkIfInstalled();

    // Try to capture prompt if it already fired
    this.tryCaptureExistingPrompt();

    this.initialized = true;
  }

  tryCaptureExistingPrompt() {
    // Some browsers might have already fired the event
    // Check if we can still get it
    if (window.deferredPrompt) {
      this.deferredPrompt = window.deferredPrompt;
      this.notifyInstallReady();
    }
  }

  checkIfInstalled() {
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone === true;
    
    if (isInstalled) {
      console.log('App is already installed');
    }
    
    return isInstalled;
  }

  isInstallable() {
    return !!this.deferredPrompt && !this.checkIfInstalled();
  }

  async install() {
    if (!this.deferredPrompt) {
      throw new Error('No install prompt available');
    }

    try {
      // Show the install prompt
      this.deferredPrompt.prompt();
      
      // Wait for user response
      const { outcome } = await this.deferredPrompt.userChoice;
      console.log(`Install outcome: ${outcome}`);
      
      // Clear the prompt
      this.deferredPrompt = null;
      
      return outcome === 'accepted';
    } catch (error) {
      console.error('Install failed:', error);
      throw error;
    }
  }

  // Event listeners
  onInstallReady(callback) {
    this.installListeners.push(callback);
    // If already ready, call immediately
    if (this.isInstallable()) {
      callback();
    }
  }

  onAppInstalled(callback) {
    this.installListeners.push(callback);
  }

  onOffline(callback) {
    this.offlineListeners.push(callback);
  }

  onUpdateAvailable(callback) {
    this.updateListeners.push(callback);
  }

  notifyInstallReady() {
    this.installListeners.forEach(cb => cb());
  }

  notifyAppInstalled() {
    this.installListeners.forEach(cb => cb());
  }

  notifyOffline() {
    this.offlineListeners.forEach(cb => cb());
  }

  notifyUpdateAvailable(registration) {
    this.updateListeners.forEach(cb => cb(registration));
  }
}

// Create singleton instance
const pwaService = new PWAService();

// Initialize as early as possible
if (typeof window !== 'undefined') {
  // Try to initialize immediately
  pwaService.init();
  
  // Also try after DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => pwaService.init());
  }
}

export default pwaService;